import styled, { css } from "styled-components";
import { TableWrapperStyled } from "styles/common.styles";

// Column-header flex-alignment fix for AntD's Table, shared verbatim by
// every Talent Mobility table wrapper that needs it (this file's own
// ClickableTableWrapperStyled, plus the plain-div wrappers in
// RedeploymentCenterV2Styles/RedeploymentReductionDetailV2Styles) — none of
// those wrappers share a common base styled-component to hang this on
// otherwise, so it's exported as a `css` fragment and interpolated into each
// one instead of copy-pasted. (TalentMobilityManagerV2's NestedTableWrapperStyled
// is NOT one of them — its table only ever centers columns, never uses the
// default flex-start alignment below, so it keeps its own smaller, genuinely
// different rule set rather than being forced into this one.)
//
// AntD's own CSS-in-JS injects justify-content: space-between on both
// .ant-table-column-sorters and .ant-table-filter-column at the same
// selector specificity these rules use, so beating it needs !important
// everywhere below — without it, the winner is whichever <style> tag was
// injected last (order-dependent, not reliable).
//
//   - Default: pin a short label's sort/filter icon right next to it
//     (flex-start) instead of AntD's far-edge default, capping
//     .ant-table-column-title to its content width so it can't push the
//     icon back out (title defaults to flex: 1, which alone would still
//     consume all the remaining space before the icon regardless of
//     justify-content).
//   - Opt-in via a column's onHeaderCell className: ".tm-col-header-center"
//     re-centers that same title+icon group for a column whose body cells
//     are align: "center" — align alone only affects the body, not the
//     header's own flex layout.
export const tmTableHeaderAlignmentStyles = css`
  .ant-table-filter-column {
    justify-content: flex-start !important;
    gap: 6px;
  }
  .ant-table-column-sorters {
    justify-content: flex-start !important;
    gap: 6px;
  }
  .ant-table-column-title {
    flex: none !important;
  }
  .tm-col-header-center .ant-table-column-sorters {
    justify-content: center !important;
  }
  .tm-col-header-center .ant-table-filter-column {
    justify-content: center !important;
  }
`;

// Every Talent Mobility table row has an onRow.onClick (navigate to detail /
// expand skill gap) — AntD doesn't apply a pointer cursor for that on its
// own, so this makes it explicit wherever a whole row is clickable.
//
// The pagination rules below mirror PageTableWrapperStyled (styles/common.styles.js,
// used by JobsLibraryV2) rather than extending it directly — that base also
// redefines the wrapper's own layout (height: 100%/flex-column vs. this
// wrapper's flex-grow/overflow-y), which none of this component's six
// consumers currently rely on. Two deliberate differences from that base:
//
//   - justify-content: flex-end, not space-between. Jobs Library's tables
//     pass a showTotal (its text takes the left slot, margin-right: auto);
//     these tables don't, and space-between with no total text scatters the
//     individual pager items across the full row width.
//   - The container padding-bottom / pagination negative-margin pair
//     reserves exactly one footer-height of scroll room so the last row can
//     scroll clear of the sticky bar instead of ending permanently beneath
//     it. The two values must stay equal (footer = 32px controls + 12px
//     padding × 2 ≈ 56px). The padding is gated on a pagination bar actually
//     rendering — tables here with pagination={false} have no negative margin
//     to cancel it, and the orphaned padding shows as a phantom scrollbar.
export const ClickableTableWrapperStyled = styled(TableWrapperStyled)`
  .ant-table-tbody > tr {
    cursor: pointer;
  }

  &:has(.ant-table-pagination) .ant-table-container {
    padding-bottom: 56px;
  }

  /* TalentMobilityRoleEmployeesTable's panel (NestedTableWrapperStyled)
     already supplies its own padding and animates its own height closed via
     grid-template-rows: 0fr (TalentMobilityManagerV2.styles.js) — but that
     collapses the panel's OWN box, one level below this <td>. AntD's
     default cell padding around it lives here, on the <td> itself, so
     without zeroing it a "closed" role would still show a fixed-height
     empty gap where that padding remains. Applied via expandedRowClassName,
     unconditionally (every role row, not just while closed) since this
     padding is pure redundant space either way. */
  .ant-table-tbody > tr.tm-role-panel-cell > td {
    padding: 0;
  }

  .ant-table-pagination {
    position: sticky;
    bottom: 0;
    margin: -56px 0 0 0 !important;
    background: ${(p) => p.theme?.colors?.white ?? "#ffffff"};
    padding: 12px 16px;
    border-top: 1px solid ${(p) => p.theme?.colors?.borderLight ?? "#E5E7EB"};
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  /* AntD's "sticky" prop (passed by every table sharing this wrapper, for
     the sticky column header) also renders its own floating "fake"
     horizontal scrollbar — .ant-table-sticky-scroll — meant to stay visible
     once the table's real one (native overflow-x: auto on
     .ant-table-content, directly under the rows) has scrolled out of view.
     It's supposed to hide itself while the real one is still on screen, but
     that visibility check reads the table content's own offsets, which the
     padding-bottom reserved above (for the sticky PAGINATION bar) throws
     off — so it never hides, and sits right on top of the real scrollbar as
     a second one. This wrapper's sticky pagination already keeps paging
     reachable at the bottom of the viewport, so the duplicate serves no
     purpose here — drop it. */
  .ant-table-sticky-scroll {
    display: none;
  }

  /* If a table here ever adds showTotal, keep its text on the left like
     Jobs Library without revisiting this file. */
  .ant-pagination-total-text {
    margin-right: auto;
  }

  ${tmTableHeaderAlignmentStyles}

  /* Title's column keeps its actual "title" a plain string (see
     useTalentMobilityManagerColumns.jsx) so the filter row stays one line
     and its funnel icon aligns exactly like every other column's. The
     "(to view potential successor)" caption is added here instead, as a
     ::after on the header cell itself — a real sibling AFTER (and outside)
     the filter row, not part of it, so it renders as a second line below
     without ever growing the row the icon aligns against. */
  .tm-title-col-header::after {
    content: "(to view potential successor)";
    display: block;
    width: 130px;
    margin-top: 2px;
    font-weight: 400;
    font-size: 12px;
    color: ${(p) => p.theme?.colors?.textSecondary ?? "#5A6A7A"};
  }
`;
